import type { ErrorEnvelope } from "../errors.js";
import { API_HEADERS } from "./conventions.js";

export interface ApiClientConfig {
  readonly baseUrl: string;
  /** Shopify session token or admin JWT — never logged or stored after use. */
  readonly authToken?: string;
  readonly correlationId?: string;
}

export type ApiSuccess<T> = { ok: true; data: T; requestId: string };
export type ApiFailure = { ok: false; error: ErrorEnvelope["error"]; requestId: string };
export type ApiResult<T> = ApiSuccess<T> | ApiFailure;

/**
 * Typed fetch wrapper that enforces contract headers and parses the
 * error envelope. Feature services call this rather than raw `fetch`
 * so the contract is applied consistently across all Vue applications.
 *
 * Note: authentication tokens are added to headers and immediately
 * discarded — they are never persisted or logged (§35.5 redaction).
 */
export async function apiFetch<T>(
  config: ApiClientConfig,
  path: string,
  init: Omit<RequestInit, "headers"> & {
    headers?: Record<string, string>;
  } = {},
): Promise<ApiResult<T>> {
  const headers: Record<string, string> = {
    "content-type": "application/json; charset=utf-8",
    ...(config.correlationId ? { [API_HEADERS.CORRELATION_ID]: config.correlationId } : {}),
    ...(config.authToken ? { authorization: `Bearer ${config.authToken}` } : {}),
    ...init.headers,
  };

  const url = `${config.baseUrl}${path}`;
  let res: Response;
  try {
    res = await fetch(url, { ...init, headers });
  } catch (err) {
    return {
      ok: false,
      error: {
        code: "INTERNAL_ERROR",
        message: "Network request failed",
        requestId: config.correlationId ?? "",
        retryable: true,
        details: null,
      },
      requestId: config.correlationId ?? "",
    };
  }

  const requestId = res.headers.get(API_HEADERS.CORRELATION_ID) ?? config.correlationId ?? "";

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as Partial<ErrorEnvelope>;
    const error = body.error ?? {
      code: "INTERNAL_ERROR",
      message: "Unexpected error response",
      requestId,
      retryable: false,
      details: null,
    };
    return { ok: false, error, requestId };
  }

  const data = (await res.json()) as T;
  return { ok: true, data, requestId };
}
